import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ISectionProps,
  PatientMessagesTableContent,
} from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { useInfiniteMessages } from "../../../../hooks/queries/messages";
import useIsMobile from "../../../../hooks/useIsMobile";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { PatientMessagesTable } from "../PatientMessagesTable";
import {
  LastMessageCell,
  NewDataCOntainer,
  TitleContainer,
  UnreadBadge,
} from "./MessagesSection.styles";

export function MessagesSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const { isMobile } = useIsMobile();
  const highlightedRows: boolean[] = [];

  const [showNewData, setShowNewData] = useState<boolean>(false);

  const {
    messages: patientMessagesTable,
    isError,
    isFetching,
    refetch,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteMessages(patient.id);

  const handleRefreshTable = () => {
    refetch();
  };

  const parsePatientMessagesToTable = useMemo(() => {
    if (!patientMessagesTable) return [];

    const applyElliples = (lastMessage: string) => {
      return lastMessage.length > 55
        ? `${lastMessage.substring(0, 55)}...`
        : lastMessage;
    };

    return patientMessagesTable.map(
      (patientMessage: PatientMessagesTableContent) => {
        if (
          (patientMessage.sentBy === "App" && !patientMessage.isRead) ||
          (patientMessage.sentBy === "Portal" &&
            patientMessage.responseDate != null &&
            !patientMessage.isPatientResponseRead)
        ) {
          highlightedRows.push(true);
        } else {
          highlightedRows.push(false);
        }

        return {
          id: patientMessage.id,
          title: patientMessage.title,
          sentBy: patientMessage.sentBy,
          isSent: patientMessage.isSent,
          sentDate: patientMessage.sentDate,
          isAnswered: patientMessage.isAnswered,
          isDelivered: patientMessage.isDelivered,
          deliveredDate: patientMessage.deliveredDate,
          isRead: patientMessage.isRead,
          readDate: patientMessage.readDate,
          response: patientMessage.response,
          createdBy: patientMessage.createdBy,
          body: patientMessage.body,
          responseType: patientMessage.responseType,
          responseOptions: patientMessage.responseOptions,
          responseDate: patientMessage.responseDate,
          isPatientResponseRead: patientMessage.isPatientResponseRead,
          isStaffResponseRead: patientMessage.isStaffResponseRead,
          sentProvider: patientMessage.sentProvider,
          createdByEmail: patientMessage.createdByEmail,
          freeResponse: patientMessage.freeResponse,
          // EN-3040: the real unread count, and only from 2 upwards — the ticket asks for the badge
          // when there is MORE THAN one unread message in the thread. A single unread is already
          // signalled by the row highlight.
          lastMessage:
            patientMessage.unreadForStaffCount > 1 ? (
              <LastMessageCell>
                {applyElliples(patientMessage.lastMessage)}
                <UnreadBadge>{patientMessage.unreadForStaffCount}</UnreadBadge>
              </LastMessageCell>
            ) : (
              applyElliples(patientMessage.lastMessage)
            ),
          lastUpdated: patientMessage.lastUpdated,
          staffName: patientMessage.staffName,
          articleId: patientMessage.articleId,
          article: patientMessage.article,
          threadId: patientMessage.threadId,
          isThreadClosed: patientMessage.isThreadClosed,
          threadStatus: patientMessage.threadStatus,
          messageCount: patientMessage.messageCount,
          unreadForStaffCount: patientMessage.unreadForStaffCount,
        };
      }
    );
  }, [patientMessagesTable]);

  useEffect(() => {
    if (
      collapsedSection.value === true &&
      !isFetching &&
      !isRefetching &&
      patientMessagesTable.length > 0
    ) {
      const collapsedDate = dayjs(collapsedSection.collapsedDate);

      patientMessagesTable.forEach((message: PatientMessagesTableContent) => {
        const sentDate = dayjs(message.sentDate);
        const deliveredDate = message.deliveredDate
          ? dayjs(message.deliveredDate)
          : dayjs("2000-01-01");
        const readDate = message.readDate
          ? dayjs(message.readDate)
          : dayjs("2000-01-01");

        if (
          sentDate > collapsedDate ||
          deliveredDate > collapsedDate ||
          readDate > collapsedDate
        ) {
          setShowNewData(true);
        }
      });
    }
  }, [patientMessagesTable]);

  const handleCollapseSection = () => {
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
    updateColapsedSections("messagesSection");
  };

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_messages")}</PageTitle>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}
      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <PatientMessagesTable
          patientId={patient.id}
          patientPhoneNumber={patient.user.phoneNumber}
          isPatientArchived={!!patient.archived}
          isLoading={isFetching && !isRefetching && !isFetchingNextPage}
          isError={isError}
          content={parsePatientMessagesToTable}
          highlightedRows={highlightedRows}
          refreshTable={handleRefreshTable}
          showSendMessageButton={!collapsedSection.value}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          useInfiniteScroll
        />
      </div>
    </>
  );
}
